import { Inject, Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';
import { HubConnectionState } from '@microsoft/signalr';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { USER_ACTIVATOR, UserActivator } from '@/app/platform/platform-core/user/activators/types';
import { SignalRHubListener } from '@common/websocket/signal-r-hub-listener';
import { serverToClientMessages } from '@common/const/server-to-client-messages';
import { clientToServerCalls } from '@common/const/client-to-server-calls';
import { NGXLogger } from 'ngx-logger';
import { environment } from '@/environments/environment';
import { Notifier, NOTIFIER } from '@shared/notifier/types';
import { AppUpdateService } from '@core/app-update/app-update.service';
import {
  ChartsChangedMessage,
  notifyChartChannelReconnected,
  notifyRemoteChartChange
} from '@common/chart-change-signals/chart-change-signals';
import { PlatformModeService, toPlatformMode } from '@/app/platform/platform-core/platform-mode/platform-mode.service';
import {
  LineTextReviewChangedMessage,
  notifyRemoteLineTextReviewChange
} from '@common/line-text-review-signals/line-text-review-signals';

@Injectable({
  providedIn: 'root'
})
export class PlatformInstanceListener extends SignalRHubListener {
  /** Wacht op een (nieuw) token om alsnog op het gebruikerskanaal te abonneren. */
  private userChannelRetry: Subscription | undefined;

  /*
   * BELANGRIJK: alle hub-handlers zijn vaste referenties (class fields), geen inline arrows.
   * afterConnected loopt bij ELKE (her)verbinding opnieuw en registreert ze dan opnieuw; SignalR
   * ontdubbelt `on(...)` enkel op functiereferentie. Met inline arrows stond er na n herverbindingen
   * n keer dezelfde handler: n update-checks per ApplicationInfoUpdate, n keer afmelden, n keer elk
   * chart-seintje. Niet terug inline zetten. Tests: platform-instance-listener.spec.ts
   * ("geen dubbele handlers na herverbinden").
   */
  private readonly onForceLogOff = (message: { body?: unknown }) => {
    const userId = message?.body as string;
    const authenticatedUser = this.userQueries.getAuthenticatedUser();
    if (authenticatedUser && authenticatedUser.id === userId) {
      this.userActivator.deactivate();
    }
  };
  private readonly onApplicationInfoUpdate = () => {
    void this.appUpdateService.checkNow('reconnect');
  };
  private readonly onUpdateClients = () => {
    this.appUpdateService.notifyPushedUpdate();
  };
  private readonly onChartsChanged = (message: ChartsChangedMessage) => notifyRemoteChartChange(message);
  private readonly onPlatformModeChanged = (message: { mode?: string }) =>
    this.platformMode.apply(toPlatformMode(message?.mode));
  /** Reviewstatus van de eigen lijnteksten gewijzigd door een andere reviewer (broadcast). */
  private readonly onLineTextReviewChanged = (message: LineTextReviewChangedMessage) =>
    notifyRemoteLineTextReviewChange(message);

  constructor(
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    @Inject(USER_ACTIVATOR)
    private readonly userActivator: UserActivator,
    private readonly appUpdateService: AppUpdateService,
    logger: NGXLogger,
    @Inject(NOTIFIER) notifier: Notifier,
    private readonly platformMode: PlatformModeService
  ) {
    super(
      `${environment.apiPaths.egituraPlatformApi}/platforminstancehub`,
      {
        name: 'Platform instance hub',
        incrementalRetryThresholdMS: 60000
      },
      notifier,
      logger
    );
  }

  async afterConnected(isReconnect: boolean = false) {
    this.listenForForceLogOff();
    this.listenForApplicationInfoUpdate();
    this.listenForApplicationUpdate();
    this.hubConnection.on(serverToClientMessages.platformInstanceHub.chartsChanged, this.onChartsChanged);
    this.hubConnection.on(serverToClientMessages.platformInstanceHub.platformModeChanged, this.onPlatformModeChanged);
    this.hubConnection.on(serverToClientMessages.platformInstanceHub.lineTextReviewChanged, this.onLineTextReviewChanged);
    // Een wijziging tijdens de onderbreking (of vóór het laden van de tab) ophalen
    void this.platformMode.load();

    await this.hubConnection.invoke(clientToServerCalls.initializeClientAfterConnectedAsync);
    await this.subscribeToUserChannel();
    if (isReconnect) {
      // Tijdens de onderbreking kunnen seintjes gemist zijn: de datacache markeert alles als oud.
      notifyChartChannelReconnected();
    }

    await super.afterConnected(isReconnect);
  }

  /**
   * Live chart-seintjes komen enkel binnen voor verbindingen die aan een gebruiker gekoppeld zijn
   * (spec chart-datacache 2026-10-06 §4). De hub zelf is anoniem; daarom bewijst de client hier met
   * zijn token wie hij is. Weigert de server (token verlopen) of is er nog geen token, dan volgt één
   * nieuwe poging zodra het token wijzigt. Seintjes zijn een extra: een fout blokkeert nooit de hub.
   */
  private async subscribeToUserChannel(): Promise<void> {
    const token = this.userQueries.getAuthenticatedUserToken();
    if (!token) {
      this.retryUserChannelOnTokenChange();
      return;
    }
    try {
      const subscribed = await this.hubConnection.invoke<boolean>(clientToServerCalls.subscribeToUserChannelAsync, token);
      if (!subscribed) {
        this.retryUserChannelOnTokenChange();
      }
    } catch (e) {
      this.logger?.warn('Abonneren op live chart-seintjes mislukt', e);
    }
  }

  private retryUserChannelOnTokenChange(): void {
    if (this.userChannelRetry) {
      return;
    }
    this.userChannelRetry = this.userQueries.authenticatedUserToken$
      .pipe(
        skip(1),
        filter(token => !!token),
        take(1)
      )
      .subscribe(() => {
        this.userChannelRetry = undefined;
        if (this.hubConnection.state === HubConnectionState.Connected) {
          void this.subscribeToUserChannel();
        }
      });
  }

  private listenForForceLogOff(): void {
    this.hubConnection.on(serverToClientMessages.platformInstanceHub.forceFrontEndAfmelden, this.onForceLogOff);
  }

  /**
   * De server stuurt dit na elke (her)verbinding. Een release herstart caddy en/of de api,
   * dus de verbinding valt weg en dit bericht volgt kort na de deploy: hét moment om te
   * kijken of er een nieuwe frontend klaarstaat.
   *
   * BELANGRIJK: de meegestuurde `version` is NIET bruikbaar als releasesignaal. Het is de
   * assembly-versie van de api, die vast op 10.0.0 staat (Egitura.Platform.Api.csproj) en
   * bij een frontend-release sowieso niet verandert. Het vroegere mechanisme vergeleek
   * precies die waarde en kon daardoor nooit afgaan. AppUpdateService vergelijkt de
   * gehashte bundles in index.html (zie app-build-fingerprint.ts).
   */
  private listenForApplicationInfoUpdate(): void {
    this.hubConnection.on(serverToClientMessages.platformInstanceHub.applicationInfoUpdate, this.onApplicationInfoUpdate);
  }

  /** Expliciete push vanuit de backend (POST /api/platform/action/update-clients). */
  private listenForApplicationUpdate(): void {
    this.hubConnection.on(serverToClientMessages.platformInstanceHub.updateClients, this.onUpdateClients);
  }
}
